import { ExposureBrand, PublishedBroadcast } from '../../services/broadcastService'

/**
 * Turning exposure into money, in one place.
 *
 * Lives at the broadcast root, not under customer/, because SUPPORT USES IT
 * TOO: the review gate shows the same figures before publishing that the
 * customer sees after. Two implementations would let the reviewer approve
 * one number and the customer receive another.
 *
 * THE MODEL IS AD EQUIVALENCY: an area-second is one second of a brand owning
 * the whole frame, so `area_seconds / 30` is how many full-screen 30-second
 * spots the exposure is worth, and the broadcast's own 30-second rate prices
 * them. The rate is derived per broadcast from its audience and CPM — a replay
 * watched by two thousand people must not price like a live game watched by
 * fifty thousand, which is the whole reason the rate is not a constant.
 *
 * NO QUALITY MULTIPLIER. Zone weights exist in the database and are all 1.00.
 * They are deliberately not applied here: weighting by placement, visibility or
 * sharpness is a judgement, and the current position is that we publish
 * measured facts and leave judgement out of the price. If that changes it
 * changes HERE, and nowhere else — the published report reads the same figures.
 *
 * MONEY IS OPTIONAL. Nothing captures viewership yet, so `rate` is usually
 * null. Every function below returns null rather than zero in that case — zero
 * is a measurement, "we were never told the audience" is not, and a report that
 * prints $0.00 against a real sponsor is worse than one that prints nothing.
 */

/** Ad equivalency is quoted against a 30-second spot. */
export const SPOT_SECONDS = 30

export interface Valuation {
  /** Full-screen 30-second spots this exposure equates to. Always available. */
  equivalents: number
  /** Null when the broadcast has no audience or CPM recorded. */
  value: number | null
}

/**
 * The broadcast's 30-second rate, or null when it cannot be derived.
 *
 * Read off the server rather than recomputed: `rate_per_30s` is already
 * audience / 1000 x CPM, and a second implementation here would be a second
 * rounding rule to keep in step.
 */
export const rateFor = (b: Pick<PublishedBroadcast, 'rate_per_30s'>): number | null => {
  const r = Number(b.rate_per_30s)
  return b.rate_per_30s === null || !Number.isFinite(r) ? null : r
}

export const valueOf = (areaSeconds: number, rate: number | null): Valuation => {
  const equivalents = areaSeconds / SPOT_SECONDS
  return { equivalents, value: rate === null ? null : equivalents * rate }
}

/**
 * A brand counts toward reported value when the server says it does.
 *
 * `counts` already folds together is_own, reviewer exclusions and mark_type, so
 * re-deriving those rules here would be a second copy of the rollup contract —
 * and the two halves of one page disagreeing about who counts is exactly the
 * bug that contract exists to prevent.
 */
export const counted = (brands: ExposureBrand[]): ExposureBrand[] =>
  brands.filter((b) => b.counts)

export interface BrandRow extends ExposureBrand {
  areaSeconds: number
  seconds: number
  equivalents: number
  value: number | null
  /** 1-based, by area-seconds. */
  valueRank: number
  /** 1-based, by time on screen. */
  timeRank: number
  /**
   * How far the brand moves between the two rankings. POSITIVE means
   * area-seconds rates it higher than raw time does.
   */
  rankShift: number
}

/**
 * The central table: every counted brand, ranked both ways.
 *
 * Both rankings are computed over the same filtered set, so a brand's two ranks
 * are comparable. Ranking time over everyone and area-seconds over the counted
 * subset would produce shifts that are an artifact of the filter rather than of
 * logo size, which is the opposite of the point this table is making.
 */
export const buildRows = (brands: ExposureBrand[], rate: number | null): BrandRow[] => {
  const rows = counted(brands).map((b) => {
    const areaSeconds = Number(b.total_area_seconds) || 0
    const { equivalents, value } = valueOf(areaSeconds, rate)
    return {
      ...b,
      areaSeconds,
      seconds: (Number(b.total_duration_ms) || 0) / 1000,
      equivalents,
      value,
      valueRank: 0,
      timeRank: 0,
      rankShift: 0
    }
  })

  return rank(rows)
}

/**
 * Assigns the three rank fields over whatever set it is given, sorted by
 * area-seconds.
 *
 * SEPARATE FROM buildRows SO RANKS CAN FOLLOW A FILTER. Ranking the whole
 * detected field and then showing a subset produces rows numbered 1 and 14
 * with nothing in between — a rank against a population the reader cannot
 * see explains nothing and looks like a bug. Whoever narrows the rows
 * re-ranks them.
 */
export const rank = (rows: BrandRow[]): BrandRow[] => {
  const byTime = [...rows].sort((a, b) => b.seconds - a.seconds)
  const timeRank = new Map(byTime.map((r, i) => [r.brand_key, i + 1]))

  const out = [...rows].sort((a, b) => b.areaSeconds - a.areaSeconds)
  out.forEach((r, i) => {
    r.valueRank = i + 1
    r.timeRank = timeRank.get(r.brand_key) ?? i + 1
    r.rankShift = r.timeRank - r.valueRank
  })
  return out
}

export interface ExposureTotals {
  areaSeconds: number
  seconds: number
  equivalents: number
  value: number | null
  brands: number
}

/**
 * Sponsors and everything else, from one ranking.
 *
 * RANKED TOGETHER, SHOWN APART. Both groups keep the ranks they earned
 * against the whole field, so a sponsor sitting 7th is 7th of everything
 * detected, not 3rd of the sponsors. Re-ranking within each group would
 * flatter the sponsor list, which is the one place a flattering number does
 * real damage.
 *
 * A hinted brand lands in `others` despite counting toward value: it names
 * no partner, so there is nothing to invoice and nobody to show it to.
 */
export const splitBySponsorship = (rows: BrandRow[]): {
  sponsors: BrandRow[]; others: BrandRow[]
} => ({
  sponsors: rows.filter((r) => r.is_sponsored),
  others: rows.filter((r) => !r.is_sponsored)
})

export const totalsOf = (rows: BrandRow[], rate: number | null): ExposureTotals => {
  const areaSeconds = rows.reduce((s, r) => s + r.areaSeconds, 0)
  const { equivalents, value } = valueOf(areaSeconds, rate)
  return {
    areaSeconds,
    seconds: rows.reduce((s, r) => s + r.seconds, 0),
    equivalents,
    value,
    brands: rows.length
  }
}

/**
 * `$1,234` — whole dollars. Cents on a planning figure imply a precision the
 * audience assumption behind it does not have.
 *
 * A real but tiny amount renders as `<$1`, never `$0`. Rounding 40 cents of
 * genuine exposure down to "$0" tells a sponsor they got nothing, which is a
 * different claim from "they got very little" and the wrong one. The same
 * reasoning as returning a dash for null: zero has to mean zero.
 */
export const formatMoney = (v: number | null, currency = 'USD'): string => {
  if (v === null || !Number.isFinite(v)) return '—'
  const fmt = (n: number) => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0
  }).format(n)
  if (v !== 0 && Math.abs(v) < 0.5) return `<${fmt(Math.sign(v))}`
  return fmt(v)
}

/**
 * A screen-area share as whole percent: `35%`, and `< 1%` for anything real
 * but smaller.
 *
 * Same rule as formatMoney, for the same reason. Rounding a genuine sliver
 * of screen down to "0%" says the brand was not visible, which is a
 * different claim from "barely visible" and the wrong one. An exact zero
 * still prints 0%, and an unmeasured share prints a dash — present, size
 * unknown, which is not the same as small.
 *
 * Whole percent is honest at this level: these are BRAND peaks, the largest
 * single moment across all a brand's appearances, and on a real roster they
 * run from about 13% to 35%. The per-APPEARANCE figures in the support
 * drawer are an order of magnitude smaller and keep their decimal, which is
 * why that one uses pct() and this does not.
 */
export const formatSharePct = (share: number | null | undefined): string => {
  if (share === null || share === undefined) return '—'
  const n = Number(share)
  if (!Number.isFinite(n)) return '—'
  const p = n * 100
  if (p !== 0 && Math.abs(p) < 0.5) return '< 1%'
  return `${Math.round(p)}%`
}

/** Area-seconds live in tenths and units: a perimeter board is ~1% of frame, so
 *  a whole-number rendering would show most of the roster as 0. */
export const formatAreaSeconds = (v: number): string => v.toFixed(2)

export const formatSeconds = (s: number): string => {
  const total = Math.round(s)
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  return m < 60 ? `${m}m ${String(total % 60).padStart(2, '0')}s`
                : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

/**
 * The three derived figures a LIBRARY ROW shows, from the rollups the list
 * endpoint returns.
 *
 * Here rather than in the components because the library table and the
 * library's aggregate cards both need them, and two definitions of "what is
 * this broadcast worth" sitting one above the other is the drift that makes
 * a column disagree with the card totalling it.
 *
 * All three return null rather than zero when the input is missing. An
 * unpriced broadcast has no value, a broadcast with nothing detected has no
 * share, and one analysed before frames were recorded has no presence —
 * none of those is the number zero.
 */
export interface RowFigures {
  value: number | null
  share: number | null
  presence: number | null
}

export const rowFigures = (b: {
  rate_per_30s?: number | null
  counted_area_seconds?: number
  sponsored_area_seconds?: number
  frames_analysed?: number | null
  frames_with_sponsor?: number | null
}): RowFigures => {
  const sponsored = Number(b.sponsored_area_seconds ?? 0)
  const counted = Number(b.counted_area_seconds ?? 0)
  const analysed = Number(b.frames_analysed ?? 0)
  const rate = b.rate_per_30s == null ? null : Number(b.rate_per_30s)

  return {
    value: rate === null ? null : (sponsored / SPOT_SECONDS) * rate,
    share: counted > 0 ? sponsored / counted : null,
    presence: analysed > 0 ? Number(b.frames_with_sponsor ?? 0) / analysed : null
  }
}
